import { useState, useCallback } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Control,
  Label,
  Combobox,
  ComboboxOption,
  Toggle,
  DatePicker,
  Divider,
  ClockIcon,
  DescriptionIcon,
} from '@forge/common';
import type { ComboboxValue } from '@forge/common';
import mockData from '@/data/mock-data.json';
import { useApiMutation } from '@/hooks/useApiMutation';
import { filesApi } from '@/api/files';
import type { CreateTransferPayload } from '@/api/files';

interface ForwardDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly fileNames: string[];
  readonly onSuccess?: () => void;
}

const ForwardDialog = ({ open, onClose, fileNames, onSuccess }: ForwardDialogProps) => {
  const [selectedRecipients, setSelectedRecipients] = useState<ComboboxValue[]>([]);
  const [notifyByEmail, setNotifyByEmail] = useState(true);
  const [requireAck, setRequireAck] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);

  const forwardMutation = useApiMutation<CreateTransferPayload, void>({
    mutationFn: async (data) => {
      await filesApi.forwardFile(data);
    },
    successMessage: showSchedule ? 'Transfer scheduled' : 'Files forwarded successfully',
    onSuccess: () => {
      onSuccess?.();
      handleClose();
    },
  });

  const handleClose = useCallback(() => {
    setSelectedRecipients([]);
    setNotifyByEmail(true);
    setRequireAck(false);
    setExpiryDate('');
    setScheduleDate('');
    setShowSchedule(false);
    onClose();
  }, [onClose]);

  const handleSend = useCallback(() => {
    const recipientIds = selectedRecipients.map((r) => String(r));
    const payload: CreateTransferPayload = {
      fileNames,
      recipientIds,
      notifyByEmail,
      requireAck,
      expiryDate: expiryDate || undefined,
      scheduledDate: showSchedule && scheduleDate ? scheduleDate : undefined,
    };
    forwardMutation.mutate(payload);
  }, [fileNames, selectedRecipients, notifyByEmail, requireAck, expiryDate, showSchedule, scheduleDate, forwardMutation]);

  if (!open) return null;

  const hasRecipients = selectedRecipients.length > 0;

  return (
    <Modal
      className="max-w-2xl"
      label="File transfer"
      title={`Forward ${fileNames.length} File${fileNames.length > 1 ? 's' : ''}`}
      onModalClose={handleClose}
      onBackdropClick={handleClose}
      footerLeft={
        <Button variant="tertiary" onClick={handleClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={!hasRecipients || (showSchedule && !scheduleDate) || forwardMutation.isSubmitting}
        >
          {forwardMutation.isSubmitting ? 'Sending…' : showSchedule ? 'Schedule' : 'Send Now'}
        </Button>
      }
    >
      <ModalContent padded className="overflow-visible">
        <div className="flex min-w-0 flex-col gap-6 overflow-visible">
          {/* Files to forward */}
          <section className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-color-tertiary">
              Files to forward
            </p>
            <div className="flex flex-wrap gap-2">
              {fileNames.map((name) => (
                <span
                  key={name}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm dark:bg-gray-800"
                >
                  <DescriptionIcon className="shrink-0" />
                  <span className="truncate max-w-[240px]">{name}</span>
                </span>
              ))}
            </div>
          </section>

          {/* Two-column body: recipients + delivery options */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Recipients */}
            <section className="flex min-w-0 flex-col">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-color-tertiary">
                Recipients
              </p>
              <Combobox
                className="w-full min-w-0"
                id="forward-recipients"
                aria-label="Recipients — clients and groups"
                placeholder="Search clients or groups..."
                multi
                value={selectedRecipients}
                onChange={(vals: ComboboxValue[]) => setSelectedRecipients(vals)}
              >
                {mockData.clientGroups.map((group) => (
                  <ComboboxOption
                    key={`group-${group.id}`}
                    value={`group-${group.id}`}
                    label={`${group.name} (${group.members.length} members)`}
                  />
                ))}
                {mockData.clients
                  .filter((c) => c.status === 'active')
                  .map((client) => (
                    <ComboboxOption
                      key={`client-${client.id}`}
                      value={`client-${client.id}`}
                      label={`${client.name} — ${client.company}`}
                    />
                  ))}
              </Combobox>
              <p className="mt-2 text-xs text-color-tertiary">
                {hasRecipients
                  ? `${selectedRecipients.length} recipient${selectedRecipients.length > 1 ? 's' : ''} selected`
                  : 'Search and select one or more clients or groups.'}
              </p>
            </section>

            {/* Delivery options */}
            <section className="flex min-w-0 flex-col">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-color-tertiary">
                Delivery options
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Label htmlFor="notify-email">Notify by email</Label>
                    <p className="text-xs text-color-tertiary">Email recipients when files arrive.</p>
                  </div>
                  <Toggle
                    id="notify-email"
                    checked={notifyByEmail}
                    onChange={(e) => setNotifyByEmail(e.target.checked)}
                  />
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <Label htmlFor="require-ack">Require acknowledgment</Label>
                    <p className="text-xs text-color-tertiary">Recipients must confirm receipt.</p>
                  </div>
                  <Toggle
                    id="require-ack"
                    checked={requireAck}
                    onChange={(e) => setRequireAck(e.target.checked)}
                  />
                </div>

                <Control hideSpacing="bottom">
                  <Label htmlFor="expiry-date">Expiry date</Label>
                  <DatePicker
                    id="expiry-date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                  />
                </Control>
              </div>
            </section>
          </div>

          <Divider />

          {/* Schedule (full width) */}
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <ClockIcon className="shrink-0" />
                <div className="min-w-0">
                  <Label htmlFor="toggle-schedule">Schedule for later</Label>
                  <p className="text-xs text-color-tertiary">Delay delivery until a chosen date.</p>
                </div>
              </div>
              <Toggle
                id="toggle-schedule"
                checked={showSchedule}
                onChange={(e) => setShowSchedule(e.target.checked)}
              />
            </div>

            {showSchedule && (
              <Control hideSpacing="bottom">
                <Label htmlFor="schedule-date">Scheduled delivery date</Label>
                <DatePicker
                  id="schedule-date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                />
              </Control>
            )}
          </section>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ForwardDialog;
